import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, APPLICANTS_COLLECTION } from "../firebase";
import {
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  EMAILJS_PUBLIC_KEY,
} from "../emailjsConfig";
import "./EnrollmentForm.css";

const COURSE_OPTIONS = [
  "Care Giver",
  "Child Minder",
  "Farming",
  "Warehouse Packer",
  "Psycho Social Counselling",
  "HIV Management",
  "Event Planning",
  "Corporate Event Management",
  "Beauty, Nails & Lashes",
  "Other/General Inquiry",
];

const TRAINING_MODES = ["Online", "Physical / In-person", "No preference"];

const initialFormState = {
  fullName: "",
  email: "",
  phone: "",
  course: "",
  trainingMode: "",
  message: "",
};

const EnrollmentForm = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(initialFormState);
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: "",
  });

  // Pre-select the course if the visitor arrived via an "Enroll Now" link
  useEffect(() => {
    const preselected = searchParams.get("course");
    if (preselected && COURSE_OPTIONS.includes(preselected)) {
      setFormData((prev) => ({ ...prev, course: preselected }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.fullName.trim()) return "Please enter your full name.";
    if (!formData.email.trim()) return "Please enter your email address.";
    if (!/^\S+@\S+\.\S+$/.test(formData.email))
      return "Please enter a valid email address.";
    if (!formData.phone.trim()) return "Please enter a phone number.";
    if (!formData.course) return "Please select a course to enroll in.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setStatus({ submitting: false, submitted: false, error: validationError });
      return;
    }

    setStatus({ submitting: true, submitted: false, error: "" });

    const applicant = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      course: formData.course,
      trainingMode: formData.trainingMode || "No preference",
      message: formData.message.trim(),
      status: "New",
      source: "website-enrollment-form",
      createdAt: serverTimestamp(),
    };

    try {
      // 1. Save to Firestore so it appears on the Admin Dashboard
      await addDoc(collection(db, APPLICANTS_COLLECTION), applicant);

      // 2. Email the details via EmailJS
      if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            full_name: applicant.fullName,
            email: applicant.email,
            phone: applicant.phone,
            course: applicant.course,
            training_mode: applicant.trainingMode,
            message: applicant.message || "N/A",
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
      }

      setStatus({ submitting: false, submitted: true, error: "" });
      setFormData(initialFormState);

      setTimeout(() => {
        setStatus({ submitting: false, submitted: false, error: "" });
      }, 6000);
    } catch (err) {
      console.error("Enrollment submission failed:", err);
      setStatus({
        submitting: false,
        submitted: false,
        error:
          "Something went wrong sending your application. Please try again, or contact us directly via WhatsApp/email.",
      });
    }
  };

  return (
    <div className="enrollment-form-wrapper">
      {status.submitted && (
        <div className="form-message success">
          Thank you! Your enrollment application has been received. Our team
          will contact you within 24 hours.
        </div>
      )}
      {status.error && <div className="form-message error">{status.error}</div>}

      <form className="enrollment-form" onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fullName">Full Name *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Your full name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+27 123 456 789"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="course">Course *</label>
            <select
              id="course"
              name="course"
              value={formData.course}
              onChange={handleChange}
              required
            >
              <option value="">Select a course</option>
              {COURSE_OPTIONS.map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="trainingMode">Preferred Training Mode</label>
            <select
              id="trainingMode"
              name="trainingMode"
              value={formData.trainingMode}
              onChange={handleChange}
            >
              <option value="">Select an option</option>
              {TRAINING_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message">Additional Information</label>
          <textarea
            id="message"
            name="message"
            rows="4"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us anything else that will help us process your enrollment (e.g. bursary interest, preferred start date)..."
          ></textarea>
        </div>

        <button
          type="submit"
          className="btn btn-primary submit-btn"
          disabled={status.submitting}
        >
          {status.submitting ? "Submitting..." : "Submit Enrollment Application"}
        </button>
      </form>
    </div>
  );
};

export default EnrollmentForm;
